import {
  translations,
  type AppLanguage,
  type Translation,
} from '@/i18n/translations';
import type { PlanningField, PlanningSection } from '@/lib/api/assessment';
import type { NormalizedPlanningView } from '@/lib/api/planning-info-mapper';

export type PlanningReportBundle = Translation['planningInfo']['report'] & {
  section1Default: string;
  section2Default: string;
  section3Default: string;
  section4Default: string;
};

export function getPlanningReportBundle(locale: AppLanguage): PlanningReportBundle {
  const pi = translations[locale].planningInfo;
  return {
    ...pi.report,
    section1Default: pi.section1Title,
    section2Default: pi.section2Title,
    section3Default: pi.section3Title,
    section4Default: pi.section4Title,
  };
}

function buildHebrewLabelMap(
  locale: AppLanguage,
  heLabels: Record<string, string>,
  localizedLabels: Record<string, string>,
): Map<string, string> {
  const map = new Map<string, string>();
  if (locale === 'he') return map;
  for (const key of Object.keys(heLabels)) {
    const he = heLabels[key as keyof typeof heLabels];
    const loc = localizedLabels[key as keyof typeof localizedLabels];
    if (he && loc) map.set(he, loc);
  }
  return map;
}

function getLabelMaps(locale: AppLanguage) {
  const he = translations.he.planningInfo.report;
  const loc = translations[locale].planningInfo.report;
  return {
    fields: buildHebrewLabelMap(locale, he.fields, loc.fields),
    blocks: buildHebrewLabelMap(locale, he.blocks, loc.blocks),
    columns: buildHebrewLabelMap(locale, he.columns, loc.columns),
    sectionTitles: buildHebrewLabelMap(locale, he.sectionTitles, loc.sectionTitles),
    categories: buildHebrewLabelMap(locale, he.categories, loc.categories),
    topics: buildHebrewLabelMap(locale, he.topics, loc.topics),
  };
}

function translateLabel(label: string, map: Map<string, string>, locale: AppLanguage): string {
  if (locale === 'he') return label;
  return map.get(label.trim()) ?? label;
}

function localizeSection(section: PlanningSection, locale: AppLanguage): PlanningSection {
  const maps = getLabelMaps(locale);
  const title = translateLabel(section.title, maps.blocks, locale);
  const titleFromSection = translateLabel(section.title, maps.sectionTitles, locale);
  const resolvedTitle =
    titleFromSection !== section.title ? titleFromSection : title;

  return {
    ...section,
    title: resolvedTitle,
    fields: section.fields?.map((f: PlanningField) => ({
      ...f,
      label: translateLabel(f.label, maps.fields, locale),
    })),
    columns: section.columns?.map((c) => translateLabel(c, maps.columns, locale)),
    items: section.items?.map((item) => ({
      label: translateLabel(item.label, maps.topics, locale),
      value: item.value,
    })),
    notes: section.notes,
  };
}

/** Translates known Hebrew labels/titles in an already-normalized view. Data values stay as returned by APIs. */
export function localizePlanningView(
  view: NormalizedPlanningView,
  locale: AppLanguage,
): NormalizedPlanningView {
  if (locale === 'he') return view;
  const maps = getLabelMaps(locale);

  const localizeTitle = (title: string) =>
    translateLabel(title, maps.sectionTitles, locale);

  return {
    ...view,
    section1Title: localizeTitle(view.section1Title),
    section1Blocks: view.section1Blocks.map((b) => localizeSection(b, locale)),
    section2Title: localizeTitle(view.section2Title),
    section2Blocks: view.section2Blocks.map((b) => localizeSection(b, locale)),
    section3Title: localizeTitle(view.section3Title),
    section3Blocks: view.section3Blocks.map((b) => localizeSection(b, locale)),
    section4Title: localizeTitle(view.section4Title),
    section4: view.section4 ? localizeSection(view.section4, locale) : undefined,
  };
}

export function formatPlanningSourceLocalized(
  source: string | undefined,
  locale: AppLanguage,
): string | undefined {
  if (!source) return undefined;
  const sources = translations[locale].planningInfo.report.sources;
  const key = source as keyof typeof sources;
  if (key in sources) return sources[key];
  return source;
}

export function formatParcelCadastralLine(
  gush: number,
  helka: number,
  locale: AppLanguage,
): string {
  return translations[locale].planningInfo.report.parcelCadastralLine
    .replace('{gush}', String(gush))
    .replace('{helka}', String(helka));
}

export function isRtlLocale(locale: AppLanguage): boolean {
  return locale === 'he' || locale === 'ar';
}

export function parseAppLanguage(value: string | null | undefined): AppLanguage | undefined {
  if (value === 'he' || value === 'en' || value === 'ar' || value === 'ru') return value;
  return undefined;
}

/** Major section titles without leading number — for block subtitle visibility. */
export function majorSectionTitleKeys(view: NormalizedPlanningView): string[] {
  return [view.section1Title, view.section2Title, view.section3Title, view.section4Title].map(
    (t) => t.replace(/^\d+\.\s*/, '').trim(),
  );
}

export function shouldShowBlockSubtitle(
  blockTitle: string,
  majorTitles: string[],
): boolean {
  const t = blockTitle.trim();
  if (!t) return false;
  return !majorTitles.some((m) => t === m || t.startsWith(`${m} —`) || t.startsWith(`${m} -`));
}
